import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { plannedGuides, referenceGuides } from '../../data/referenceGuides';
import { SystemHandoffMap } from '../../components/reference/SystemHandoffMap';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { CTABanner } from '../../components/CTABanner';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const ACCENT_TEXT = {
  inventory: 'text-guide-inventory',
  manufacturing: 'text-guide-manufacturing',
  procurement: 'text-guide-procurement'
} as const;
export function ReferenceGuidesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useDocumentMeta({
    title: 'Reference Guides | Thruline',
    description: 'Vendor-neutral reference guides on how ERP, MES, WMS, inventory, and procurement systems divide the work — what each layer owns and where the hand-offs break.'
  });
  return <div className="min-h-screen bg-navy text-white" data-id="element-1230" data-mp="3rt09x3m8jl5v">
      <section className="relative pt-32 pb-16 px-6 overflow-hidden" data-id="element-1231" data-mp="9dg0grizs2qu">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-1232" data-mp="9fssxymdlvi5">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[520px] rounded-full animate-glow-pulse" style={{
          background: 'radial-gradient(ellipse at center, rgba(108,92,231,.16) 0%, rgba(200,80,192,.05) 40%, transparent 70%)'
        }} data-id="element-1233" data-mp="4kwo1xw1jdnm" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-1234" data-mp="sc24of9t29yz">
          {/* The hero call-out leads on every breakpoint; the hand-off map
              supports it and follows underneath on narrow screens. */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="order-2 min-w-0" data-id="element-1235" data-mp="2p5nb1rgmhxhv">
            <SystemHandoffMap data-id="element-1236" data-mp="y2wm7ecchyyh" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="order-1" data-id="element-1237" data-mp="12zn9omqhbwtx">
            <span className="text-violet-link text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1238" data-mp="1bzs847dp3kvy">
              Reference Guides
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-6" data-id="element-1239" data-mp="1lqmvjf2ogqiz">
              Understand the systems that run your operation
            </h1>
            <p className="text-lg text-white/60 leading-relaxed mb-8" data-id="element-1240" data-mp="1ur2zepnc1zod">
              Vendor-neutral reference packets on how ERP, MES, WMS, inventory, and procurement
              systems actually divide the work — what each layer owns, where the hand-offs break,
              and how to tell which system you need.
            </p>
            <a href="#guides" className="inline-flex items-center gap-2 min-h-[44px] px-6 py-3 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-colors" data-id="element-1241" data-mp="3f82aw7c9hxto">
              Choose a system to understand
              <ArrowRightIcon aria-hidden="true" className="w-4 h-4" data-id="element-1242" data-mp="36a9t8wj2ie34" />
            </a>
          </motion.div>
        </div>
      </section>

      <section id="guides" className="relative px-6 pb-20 scroll-mt-28" data-id="element-1243" data-mp="2ryqcldp860e">
        <div className="max-w-6xl mx-auto" data-id="element-1244" data-mp="2tt9bu5eto1u">
          <h2 className="sr-only" data-id="element-1245" data-mp="3u5kwj0tn7uww">All reference guides</h2>

          <motion.ul variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6 list-none" data-id="element-1246" data-mp="3u41izhzdfxyt">
            {referenceGuides.map(guide => <motion.li key={guide.slug} variants={fadeUp} transition={{
            duration: 0.4
          }} data-id="element-1247" data-mp="lqb3ei7qe019">
                {/* Stripped back per design review: category, title, the
                    diagnostic question, and read time. No icon tile, no chips —
                    that stack is the most recognisable AI-generated layout. */}
                <Link to={`/resources/reference-guides/${guide.slug}`} className="group flex flex-col h-full rounded-2xl border border-white/[0.08] bg-white/[0.02] p-7 hover:border-violet-link/30 hover:bg-white/[0.04] transition-all duration-300" data-id="element-1248" data-mp="3si47nvmy90m9">
                  <span className={`${ACCENT_TEXT[guide.accent]} text-xs font-semibold tracking-widest uppercase`} data-id="element-1249" data-mp="szl2111ixc9l">
                    {guide.category}
                  </span>
                  <h3 className="text-xl font-bold text-white mt-2.5 mb-4 leading-snug group-hover:text-violet-link transition-colors" data-id="element-1250" data-mp="k82komsucqex">
                    {guide.title}
                  </h3>
                  <p className="text-white/60 text-base leading-relaxed mb-6" data-id="element-1251" data-mp="1coddav4gecop">{guide.question}</p>
                  <div className="mt-auto flex items-center justify-between pt-4 border-t border-white/[0.08]" data-id="element-1252" data-mp="1m370wq0678vc">
                    <span className="text-white/50 text-sm" data-id="element-1253" data-mp="1i2dek12dm24k">{guide.readMinutes} min read</span>
                    <span className="inline-flex items-center gap-2 text-violet-link text-sm font-medium group-hover:gap-3 transition-all" data-id="element-1254" data-mp="1vtrxy90745c5">
                      Read guide
                      <ArrowRightIcon aria-hidden="true" className="w-4 h-4" data-id="element-1255" data-mp="1qwi148ah8rea" />
                    </span>
                  </div>
                </Link>
              </motion.li>)}
          </motion.ul>

          <div className="mt-6 rounded-2xl border border-dashed border-white/[0.1] bg-white/[0.01] p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1256" data-mp="z5c80bt6btyi">
            <div data-id="element-1257" data-mp="18231cggccupe">
              <p className="text-white/70 text-sm font-semibold mb-1" data-id="element-1258" data-mp="185quvflsdh44">More guides in progress</p>
              <p className="text-white/50 text-sm" data-id="element-1259" data-mp="2ok0ha46gwvsb">
                {plannedGuides.map(g => g.title).join(' · ')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <CTABanner data-id="element-1260" data-mp="2rj8ymcoqrr4z" />
    </div>;
}